import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, FileText, Settings, LogOut, Users, Package, Clock, CheckCircle, 
  TrendingUp, BarChart3, AlertTriangle, Download, Calendar, Wrench,
  Bell, Moon, Sun, RefreshCw, AlertCircle, Target, Activity, DollarSign, Eye, Globe, Tag,
  Mail, Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [slaAlerts, setSlaAlerts] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchAllData = useCallback(async () => {
    try {
      const [statsRes, realtimeRes, kpisRes, slaRes, fraudRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/realtime/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/metrics/kpis`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/sla/alerts?acknowledged=false`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/fraud/alerts?resolved=false`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setRealtimeStats(realtimeRes.data);
      setKpis(kpisRes.data);
      setSlaAlerts(slaRes.data.slice(0, 5));
      setFraudAlerts(fraudRes.data.slice(0, 5));
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAllData]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/export/${type}?format=csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/api/admin/sla/alerts/${alertId}/acknowledge`, {}, { withCredentials: true });
      setSlaAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const statCards = [
    { 
      title: 'Garantías Totales', 
      value: stats?.total_warranties || 0, 
      icon: Shield, 
      color: 'bg-[#0047FF]',
      change: realtimeStats?.today_new_warranties ? `+${realtimeStats.today_new_warranties} hoy` : null
    },
    { 
      title: 'Garantías Pendientes', 
      value: stats?.pending_warranties || 0, 
      icon: Clock, 
      color: 'bg-[#F59E0B]',
      change: null
    },
    { 
      title: 'En Reparación', 
      value: realtimeStats?.in_repair || 0, 
      icon: Wrench, 
      color: 'bg-[#8B5CF6]',
      change: null
    },
    { 
      title: 'Listas para Enviar', 
      value: realtimeStats?.ready_to_ship || 0, 
      icon: Package, 
      color: 'bg-[#10B981]',
      change: null
    },
    { 
      title: 'Usuarios', 
      value: stats?.total_users || 0, 
      icon: Users, 
      color: 'bg-[#06B6D4]',
      change: null
    }
  ];

  const kpiCards = kpis ? [
    {
      title: 'Tiempo Medio Resolución',
      value: `${kpis.avg_resolution_time_hours}h`,
      icon: Clock,
      color: 'text-[#0047FF]'
    },
    {
      title: 'Tasa Completado',
      value: `${kpis.completion_rate}%`,
      icon: Target,
      color: 'text-[#10B981]'
    },
    {
      title: 'Cumplimiento SLA',
      value: `${kpis.sla_compliance_rate}%`,
      icon: Activity,
      color: kpis.sla_compliance_rate >= 90 ? 'text-[#10B981]' : 'text-[#F59E0B]'
    },
    {
      title: 'Satisfacción Cliente',
      value: kpis.customer_satisfaction > 0 ? `${kpis.customer_satisfaction}/5` : 'N/A',
      icon: CheckCircle,
      color: 'text-[#8B5CF6]'
    }
  ] : [];

  const menuItems = [
    { title: 'Dashboard', icon: BarChart3, link: '/admin', count: null },
    { title: 'Analytics', icon: Eye, link: '/admin/analytics', count: null },
    { title: 'Gestionar Garantías', icon: Shield, link: '/admin/warranties', count: stats?.pending_warranties },
    { title: 'Gestionar Facturas', icon: FileText, link: '/admin/invoices', count: stats?.pending_invoices },
    { title: 'Clientes', icon: Users, link: '/admin/clients', count: null },
    { title: 'SEO & Contenido', icon: Globe, link: '/admin/seo', count: null },
    { title: 'Marketing', icon: Tag, link: '/admin/marketing', count: null },
    { title: 'Email Marketing', icon: Mail, link: '/admin/email-marketing', count: null },
    { title: 'Retención', icon: Heart, link: '/admin/retention', count: null },
    { title: 'Calendario Envíos', icon: Calendar, link: '/admin/calendar', count: null },
    { title: 'Técnicos', icon: Wrench, link: '/admin/technician', count: null },
    { title: 'Configuración', icon: Settings, link: '/admin/settings', count: null }
  ];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-[#F5F5F7]'}`} data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-[#0A0A0A] text-white flex flex-col z-40">
        <div className="p-6">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0047FF] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <div>
              <span className="font-heading font-bold text-lg">Arreglatelo</span>
              <p className="text-xs text-[#A1A1AA]">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-4 space-y-1">
          {menuItems.map((item, i) => (
            <Link
              key={i}
              to={item.link}
              className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-white/10 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <item.icon className="w-5 h-5 text-[#A1A1AA] group-hover:text-white transition-colors" />
                <span className="text-sm">{item.title}</span>
              </div>
              {item.count > 0 && (
                <span className="bg-[#0047FF] text-xs px-2 py-0.5 rounded-full">{item.count}</span>
              )}
            </Link>
          ))}
        </nav>

        {/* Alert badges */}
        <div className="px-4 mb-4 space-y-2">
          {slaAlerts.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#F59E0B]/20 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-xs text-[#F59E0B]">{slaAlerts.length} alertas SLA</span>
            </div>
          )}
          {fraudAlerts.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#EF4444]/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-[#EF4444]" />
              <span className="text-xs text-[#EF4444]">{fraudAlerts.length} alertas fraude</span>
            </div>
          )}
        </div>

        {/* User section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0047FF] rounded-full flex items-center justify-center">
                <span className="text-sm font-medium">{user?.name?.[0] || 'A'}</span>
              </div>
              <span className="text-sm truncate">{user?.name || 'Admin'}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="flex-1 text-[#A1A1AA] hover:text-white"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="flex-1 text-[#A1A1AA] hover:text-white"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : ''}`}>Dashboard</h1>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-[#52525B]'}`}>
              Última actualización: {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'border-[#10B981] text-[#10B981]' : ''}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('warranties')}
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          {statCards.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border p-4`}
            >
              <div className={`w-10 h-10 ${card.color} rounded-lg flex items-center justify-center mb-3`}>
                <card.icon className="w-5 h-5 text-white" />
              </div>
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : ''}`}>{card.value}</p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-[#52525B]'}`}>{card.title}</p>
              {card.change && (
                <p className="text-xs text-[#10B981] mt-1">{card.change}</p>
              )}
            </motion.div>
          ))}
        </div>

        {/* KPIs Row */}
        {kpiCards.length > 0 && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            {kpiCards.map((kpi, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border p-4`}
              >
                <div className="flex items-center justify-between mb-2">
                  <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                  <span className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</span>
                </div>
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-[#52525B]'}`}>{kpi.title}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Status breakdown + Alerts */}
        <div className="grid grid-cols-3 gap-6">
          {/* Status Breakdown */}
          <div className={`col-span-2 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border p-6`}>
            <h3 className={`font-semibold mb-4 ${theme === 'dark' ? 'text-white' : ''}`}>Desglose por Estado</h3>
            <div className="space-y-3">
              {stats?.warranty_by_status && Object.entries(stats.warranty_by_status).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-[#52525B]'}`}>{status}</span>
                  <div className="flex items-center gap-3">
                    <div className={`w-32 h-2 ${theme === 'dark' ? 'bg-gray-700' : 'bg-[#E5E7EB]'} rounded-full overflow-hidden`}>
                      <motion.div
                        className="h-full bg-[#0047FF] rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${(count / stats.total_warranties) * 100}%` }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      />
                    </div>
                    <span className={`text-sm font-medium w-8 text-right ${theme === 'dark' ? 'text-white' : ''}`}>{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SLA Alerts */}
          <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : ''}`}>Alertas SLA</h3>
              <span className={`text-xs px-2 py-1 rounded-full ${
                slaAlerts.length > 0 ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#10B981]/20 text-[#10B981]'
              }`}>
                {slaAlerts.length > 0 ? `${slaAlerts.length} pendientes` : 'Todo OK'}
              </span>
            </div>
            
            <div className="space-y-3">
              <AnimatePresence>
                {slaAlerts.map((alert) => (
                  <motion.div
                    key={alert.alert_id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className={`p-3 rounded-lg ${
                      alert.severity === 'high' 
                        ? 'bg-[#EF4444]/10 border border-[#EF4444]/20' 
                        : 'bg-[#F59E0B]/10 border border-[#F59E0B]/20'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`text-xs font-medium ${
                          alert.severity === 'high' ? 'text-[#EF4444]' : 'text-[#F59E0B]'
                        }`}>
                          {alert.warranty_id.slice(0, 12).toUpperCase()}
                        </p>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-[#52525B]'}`}>
                          +{alert.hours_exceeded}h en "{alert.status}"
                        </p>
                      </div>
                      <button
                        onClick={() => acknowledgeAlert(alert.alert_id)}
                        className={`text-xs ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-[#52525B] hover:text-[#0A0A0A]'}`}
                      >
                        Aceptar
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {slaAlerts.length === 0 && (
                <div className="text-center py-6">
                  <CheckCircle className="w-8 h-8 text-[#10B981] mx-auto mb-2" />
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-[#52525B]'}`}>
                    Sin alertas pendientes
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fraud Alerts (if any) */}
        {fraudAlerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border p-6`}
          >
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-[#EF4444]" />
              <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : ''}`}>Alertas de Fraude</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {fraudAlerts.map((alert) => (
                <div
                  key={alert.alert_id}
                  className="p-4 bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      alert.severity === 'high' ? 'bg-[#EF4444] text-white' : 'bg-[#F59E0B] text-white'
                    }`}>
                      {alert.severity}
                    </span>
                    <span className="text-xs text-[#EF4444]">{alert.alert_type}</span>
                  </div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-[#52525B]'}`}>
                    {alert.description}
                  </p>
                  <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-[#A1A1AA]'}`}>
                    {alert.warranty_id.slice(0, 12).toUpperCase()}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-4 gap-4">
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => navigate('/admin/warranties')}
          >
            <Shield className="w-6 h-6 text-[#0047FF]" />
            <span>Gestionar RMAs</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => navigate('/admin/invoices')}
          >
            <FileText className="w-6 h-6 text-[#10B981]" />
            <span>Facturas</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => handleExport('warranties')}
          >
            <Download className="w-6 h-6 text-[#8B5CF6]" />
            <span>Exportar CSV</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => navigate('/admin/settings')}
          >
            <Settings className="w-6 h-6 text-[#52525B]" />
            <span>Configuración</span>
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
